import { useEffect, useRef, useState } from 'react'
import { Loader2, MapPin, Search } from 'lucide-react'
import apiClient from '@/api/client'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import type { PlaceSuggestion, ResolvedPlace } from '@/lib/maps/types'

// Address search for POS maps (F-BE-N90). Replaces Google's
// PlaceAutocompleteElement: the lookup runs through our backend with
// Bhookly's server key, so no Places-enabled key ever sits in the page.

function newSession(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
}

type Props = {
  onPick: (place: ResolvedPlace) => void
  /** Prefer results near here (the map centre). */
  near?: () => { lat: number; lng: number } | null
  placeholder?: string
  className?: string
}

export function PlaceSearch({ onPick, near, placeholder = 'Search a city or area', className }: Props) {
  const [q, setQ] = useState('')
  const [items, setItems] = useState<PlaceSuggestion[]>([])
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(-1)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  // One Google billing session = the keystrokes + the pick that ends them.
  const session = useRef(newSession())
  const seq = useRef(0)
  const nearRef = useRef(near)
  nearRef.current = near

  useEffect(() => {
    const term = q.trim()
    if (term.length < 2) {
      setItems([])
      setError('')
      return
    }
    const my = ++seq.current
    const t = window.setTimeout(async () => {
      setLoading(true)
      try {
        const c = nearRef.current?.() ?? null
        const res = await apiClient.mapsAutocomplete({ q: term, session: session.current, ...(c ?? {}) })
        if (my !== seq.current) return
        setItems(res.data ?? [])
        setError('')
        setActive(-1)
        setOpen(true)
      } catch (e) {
        if (my !== seq.current) return
        setItems([])
        setError(e instanceof Error ? e.message : 'Search isn’t available right now.')
        setOpen(true)
      } finally {
        if (my === seq.current) setLoading(false)
      }
    }, 300)
    return () => window.clearTimeout(t)
  }, [q])

  async function pick(s: PlaceSuggestion) {
    setOpen(false)
    setQ(s.main)
    const token = session.current
    session.current = newSession()
    try {
      const res = await apiClient.mapsPlace({ id: s.place_id, session: token })
      if (res.data) onPick(res.data)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Couldn’t open that place.')
      setOpen(true)
    }
  }

  return (
    <div className={cn('relative', className)}>
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => (items.length > 0 || error) && setOpen(true)}
        onBlur={() => window.setTimeout(() => setOpen(false), 120)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault()
            setActive((i) => Math.min(i + 1, items.length - 1))
          } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setActive((i) => Math.max(i - 1, 0))
          } else if (e.key === 'Enter') {
            const s = items[active >= 0 ? active : 0]
            if (s) {
              e.preventDefault()
              void pick(s)
            }
          } else if (e.key === 'Escape') {
            setOpen(false)
          }
        }}
        placeholder={placeholder}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        className="pl-9 pr-9"
      />
      {loading && <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />}
      {open && (items.length > 0 || error) && (
        <div className="absolute inset-x-0 top-full z-20 mt-1 overflow-hidden rounded-lg border bg-popover text-sm shadow-md" role="listbox">
          {error ? (
            <p className="px-3 py-2 text-muted-foreground">{error}</p>
          ) : (
            items.map((s, i) => (
              <button
                key={s.place_id}
                type="button"
                role="option"
                aria-selected={i === active}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => void pick(s)}
                className={cn('flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-accent', i === active && 'bg-accent')}
              >
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="min-w-0">
                  <span className="block truncate">{s.main}</span>
                  {s.secondary && <span className="block truncate text-xs text-muted-foreground">{s.secondary}</span>}
                </span>
              </button>
            ))
          )}
          <p className="border-t px-3 py-1 text-right text-[10px] text-muted-foreground">Powered by Google</p>
        </div>
      )}
    </div>
  )
}
